import React from "react";
import { motion } from "framer-motion";

const FRONT = "/assets/qheart-front.png";
const BACK = "/assets/qheart-back.png";

// Decorative blob
const Blob = ({ className, delay = 0, gradId }) => (
  <motion.svg
    className={className}
    viewBox="0 0 200 200"
    xmlns="http://www.w3.org/2000/svg"
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 1.2, delay, ease: "easeOut" }}
    aria-hidden="true"
  >
    <defs>
      <radialGradient id={gradId} cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#E11D48" stopOpacity="0.85" />
        <stop offset="100%" stopColor="#FFE4E6" stopOpacity="0.6" />
      </radialGradient>
    </defs>
    <path
      fill={`url(#${gradId})`}
      d="M44.3,-58.3C55.8,-46.3,62.3,-31.3,65.7,-15.6C69.1,0.2,69.6,16.6,62.6,28.6C55.5,40.6,41.1,48.2,26.3,55.3C11.4,62.4,-3.7,68.9,-19.1,67.8C-34.5,66.8,-50.3,58.2,-60.2,44.7C-70,31.2,-74,12.8,-70.9,-3.5C-67.8,-19.7,-57.6,-33.8,-45.2,-45.9C-32.8,-58,-18.2,-68.1,-1.7,-66.1C14.7,-64,32.7,-70.3,44.3,-58.3Z"
      transform="translate(100 100)"
    />
  </motion.svg>
);

// Floating QHeart image
const FloatingQHeart = ({
  wrapperClass,
  src = FRONT,
  alt = "QHeart",
  rotate = 0,
  delay = 0,
  duration = 6,
  y = 14,
  dropShadow = "drop-shadow(0 30px 40px rgba(225,29,72,0.25))",
}) => (
  <div className={wrapperClass}>
    <motion.div
      className="w-full h-full"
      initial={{ opacity: 0, y: 24, rotate: rotate - 6 }}
      animate={{ opacity: 1, y: [0, -y, 0], rotate }}
      transition={{
        opacity: { duration: 1, delay, ease: "easeOut" },
        rotate: { duration: 1, delay, ease: "easeOut" },
        y: { duration, delay, repeat: Infinity, ease: "easeInOut" },
      }}
      style={{ filter: dropShadow }}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-contain select-none"
        draggable="false"
      />
    </motion.div>
  </div>
);

export default function HeroArt() {
  return (
    <div
      className="relative w-full aspect-square max-w-[560px] mx-auto"
      data-testid="hero-art"
    >
      {/* Soft background cream circle */}
      <div className="absolute inset-6 rounded-full bg-[#FFFBFB] border border-[#FFE4E6]" />

      {/* Gradient blobs */}
      <Blob
        className="absolute -top-6 -left-6 w-64 h-64 opacity-60"
        delay={0.1}
        gradId="blob-a"
      />
      <Blob
        className="absolute bottom-0 right-0 w-56 h-56 opacity-40"
        delay={0.3}
        gradId="blob-b"
      />

      {/* Large centered QHeart — front */}
      <FloatingQHeart
        wrapperClass="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[62%] aspect-square"
        src={FRONT}
        alt="QHeart Vorderseite"
        duration={7}
        delay={0.2}
        y={10}
        rotate={-4}
      />

      {/* Accent — small back QHeart showing the QR (storytelling hint) */}
      <FloatingQHeart
        wrapperClass="absolute top-4 right-4 w-20 sm:w-24 aspect-square"
        src={BACK}
        alt="QHeart Rückseite"
        duration={5.5}
        delay={0.5}
        y={16}
        rotate={10}
        dropShadow="drop-shadow(0 12px 18px rgba(10,10,10,0.15))"
      />

      {/* Accent — small front */}
      <FloatingQHeart
        wrapperClass="absolute bottom-6 left-4 w-16 sm:w-20 aspect-square"
        src={FRONT}
        alt="QHeart klein"
        duration={6.5}
        delay={0.7}
        y={12}
        rotate={-12}
        dropShadow="drop-shadow(0 12px 18px rgba(225,29,72,0.2))"
      />

      {/* Sparkle dots */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
        className="absolute inset-0 pointer-events-none"
      >
        <span className="absolute left-[18%] top-[22%] w-1.5 h-1.5 rounded-full bg-[#E11D48]" />
        <span className="absolute right-[32%] top-[18%] w-1 h-1 rounded-full bg-[#0A0A0A]" />
        <span className="absolute left-[22%] bottom-[22%] w-1 h-1 rounded-full bg-[#E11D48]" />
        <span className="absolute right-[14%] bottom-[28%] w-1.5 h-1.5 rounded-full bg-[#0A0A0A]" />
      </motion.div>
    </div>
  );
}
